import { useStores, type Project } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Text, YStack } from '@/components/ui';
import { SaveableField } from '@/components/ui/SaveableField';
import { SettingsSection } from '@/features/settings/settings-section';

export const ProjectInstructionsTab = observer(function ProjectInstructionsTab({
  project,
}: {
  project: Project;
}) {
  const { projects } = useStores();
  const canEdit = project.capabilities.canEditContent;
  const [inputInstructions, setInputInstructions] = useState(
    project.instructions ?? ''
  );
  const savedInstructions = project.instructions ?? '';
  const instructionsDirty = canEdit && inputInstructions !== savedInstructions;

  useEffect(() => {
    setInputInstructions(project.instructions ?? '');
  }, [project.id, project.instructions]);

  const includeOutsideProjectChats = !project.limitChatContextToProject;

  const handleIncludeOutsideProjectChatsChange = (include: boolean) => {
    void projects.updateProject(project, {
      limitChatContextToProject: !include,
    });
  };

  const handleIncludeScreenActivityChange = (include: boolean) => {
    void projects.updateProject(project, {
      disableScreenContextSearch: !include,
    });
  };

  return (
    <YStack className="gap-6">
      <YStack className="w-full gap-3">
        <SettingsSection.GroupHeader>
          Project instructions
        </SettingsSection.GroupHeader>
        <SaveableField
          id="project-instructions"
          className="min-h-[12rem] max-h-[32rem] text-sm resize-none [field-sizing:content]"
          wrapperClassName="mt-0"
          placeholder={
            canEdit
              ? 'Customize responses in this project with your own instructions.'
              : 'No instructions set.'
          }
          value={inputInstructions}
          dirty={instructionsDirty}
          onChange={setInputInstructions}
          onSave={() => {
            if (instructionsDirty) {
              void projects.updateProject(project, {
                instructions: inputInstructions,
              });
            }
          }}
          onDiscard={() => setInputInstructions(savedInstructions)}
          enableVoiceInput={canEdit}
          busy={!canEdit}
          statusContent={
            canEdit ? undefined : (
              <Text className="text-xs text-foreground-muted">
                You have view access. Ask an editor to update the instructions.
              </Text>
            )
          }
        />
      </YStack>
      <SettingsSection.Group title="Context sources">
        <SettingsSection.Item>
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Conversations and meetings in this project
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Always available in this project&rsquo;s chats.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <SettingsSection.Switch
            checked
            disabled
            className="cursor-not-allowed"
          />
        </SettingsSection.Item>

        <SettingsSection.Seperator />

        <SettingsSection.Item>
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Conversations and meetings from other projects
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Include chats and meeting notes from other projects when
              answering.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <SettingsSection.Switch
            checked={includeOutsideProjectChats}
            onCheckedChange={handleIncludeOutsideProjectChatsChange}
            disabled={!canEdit}
          />
        </SettingsSection.Item>

        <SettingsSection.Seperator />

        <SettingsSection.Item>
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Past screen activity
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Include information previously captured from your screen when
              answering.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <SettingsSection.Switch
            checked={!project.disableScreenContextSearch}
            onCheckedChange={handleIncludeScreenActivityChange}
          />
        </SettingsSection.Item>
      </SettingsSection.Group>
    </YStack>
  );
});
